<script setup lang="ts">
import { RouterView, useRouter, useRoute } from 'vue-router'
import { onMounted, watch, computed } from 'vue'
import { storeToRefs } from 'pinia'
import AppNavigation from './components/layout/AppNavigation.vue'
import AppFooter from './components/layout/AppFooter.vue'
import { useAuth } from '@/composables/useAuth'
import { useCustomerStore } from '@/stores/customer'
import { ContractType } from '@/types/customer'

const { isAuthenticated } = useAuth()
const router = useRouter()
const route = useRoute()
const customerStore = useCustomerStore()
const { selectedClientId } = storeToRefs(customerStore)

// Map route names to component identifiers for stable RouterView key
// This ensures components only remount when the actual view component changes,
// not when IDs within the same component change
const routeComponentKey = computed(() => {
  const routeName = route.name as string | null
  if (!routeName) return route.path

  // Map routes that use the same component to the same key
  const componentMap: Record<string, string> = {
    // MessagesView component
    'messages': 'MessagesView',
    // InsuranceCaseDetailView component
    'insurance-case-detail': 'InsuranceCaseDetailView',
    // ContractDetailView component
    'contract-detail': 'ContractDetailView',
    // SubcontractDetailView component
    'subcontract-detail': 'SubcontractDetailView',
    // NewMessageView component
    'new-message': 'NewMessageView',
    'new-message-contract': 'NewMessageView',
    'new-message-subcontract': 'NewMessageView',
    'new-message-insurance-case': 'NewMessageView',
  }

  return componentMap[routeName] || routeName
})

// Sync route params to route context when route changes
// This must be in App.vue because useCustomerStore() uses inject(),
// which only works inside Vue component setup functions
watch(
  () => route.params,
  async (newParams) => {
    if (isAuthenticated.value) {
      // Skip validation for routes without clientId parameter
      if (!newParams.clientId) {
        return
      }

      // Extract IDs based on route name for flat routes.
      // Apply them before any await: the view mounts in this flush and would
      // otherwise keep the previous subcontract (and a late response would write it back).
      const routeName = route.name as string
      let contractId: number | null = null
      const contractType: ContractType | null = null // Will be loaded from API
      let subcontractId: number | null = null
      let caseId: number | null = null

      if (routeName === 'contract-detail' && newParams.contractId) {
        contractId = Number(newParams.contractId)
        // contractType will be loaded from API
      } else if (routeName === 'subcontract-detail' && newParams.subcontractId) {
        subcontractId = Number(newParams.subcontractId)
        // contractId and contractType will be loaded from API (parent hierarchy)
      } else if (routeName === 'insurance-case-detail' && newParams.caseId) {
        caseId = Number(newParams.caseId)
        // contractId, contractType, subcontractId will be loaded from API
      } else if (routeName === 'new-message-contract' && newParams.contractId) {
        contractId = Number(newParams.contractId)
        // contractType will be loaded from API
      } else if (routeName === 'new-message-subcontract' && newParams.subcontractId) {
        subcontractId = Number(newParams.subcontractId)
        // contractId and contractType will be loaded from API (parent hierarchy)
      } else if (routeName === 'new-message-insurance-case' && newParams.caseId) {
        caseId = Number(newParams.caseId)
        // contractId, contractType, subcontractId will be loaded from API
      }

      const fromUrl = Number(newParams.clientId)
      const knownClientId = !Number.isNaN(fromUrl) && fromUrl !== 0 ? fromUrl : null
      if (knownClientId !== null) {
        const previous = customerStore.routeContext
        const sameContract = contractId !== null && contractId === previous.contractId
        // Subcontract URLs do not include the parent. Keep the parent already in context
        // so the page does not drop it and load the contract detail again.
        const keepParent =
          subcontractId !== null && contractId === null && previous.contractId !== null
        customerStore.setRouteContext({
          clientId: knownClientId,
          contractId: keepParent ? previous.contractId : contractId,
          contractType: keepParent || sameContract ? previous.contractType : contractType,
          subcontractId,
          caseId,
        })
      }

      await customerStore.ensureClientsLoaded()

      // Validate clientId exists in user's accessible clients
      let clientId = Number(newParams.clientId)
      clientId = clientId == 0 ? customerStore.routeContext.clientId ?? 0 : clientId;
      const isValid = customerStore.clients?.some((c: { id: number }) => c.id === clientId)

      if (!isValid) {
        // Invalid clientId - redirect to home
        router.push({ name: 'home', params: { clientId: customerStore.clients?.[0]?.id?.toString() || '' } })
        return
      }

      // clientId 0 is resolved only after clients load; a concrete URL id was applied above.
      if (knownClientId === null || knownClientId !== clientId) {
        customerStore.setRouteContext({
          clientId: clientId,
          contractId: contractId,
          contractType: contractType,
          subcontractId: subcontractId,
          caseId: caseId,
        })
      }
    } else {
      // Clear route context when navigating away from routes with clientId
      //customerStore.clearRouteContext()
    }
  },
  { immediate: true }
)

// Note: With flat routes, we no longer update the route when hierarchy loads.
// The route remains stable once navigated to, and parent hierarchy is loaded
// automatically by the store for breadcrumbs and context.
// The routeContext → route watcher has been removed as it's no longer needed.

// When the client combo changes, keep the same route and swap `clientId` in the URL.
// Detail / new-message screens register useRedirectOnClientChange(...) to strip invalid resource params.
watch(selectedClientId, (newClientId, oldClientId) => {
  if (oldClientId !== undefined && newClientId !== oldClientId) {
    const currentRoute = router.currentRoute.value
    if (currentRoute.params.clientId && currentRoute.name) {
      router.push({
        name: currentRoute.name,
        params: {
          ...currentRoute.params,
          clientId: String(newClientId ?? ''),
        },
        query: currentRoute.query,
      })
    }
  }
})

onMounted(async () => {
  //await checkAuth()
})
</script>

<template>
  <div class="common-layout">
    <el-container class="">
      <el-header class="app-nav-header">
        <!-- Header with Navigation and Breadcrumbs -->
        <AppNavigation />
      </el-header>
      <!-- Main Content -->
      <el-main class="">
        <div class="app-content">
          <RouterView :key="routeComponentKey" v-slot="{ Component }">
            <component :is="Component" />
            <AppFooter />
          </RouterView>
        </div>
      </el-main>

    </el-container>
  </div>
</template>

<style scoped>
.common-layout {
  height: 100vh;
  overflow: hidden;
}

.el-container {
  height: 100%;
  overflow: hidden;
}

.app-nav-header {
  background: var(--el-bg-color);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  padding: 0;
  display: flex;
  height: auto;
}
.el-main {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
}
.app-content {
  flex: 1 1 auto;
  overflow: auto;
}
</style>
